import { CmpApi } from "@iabtechlabtcf/cmpapi";
import { SDK_NAME } from "./constants";
import type { CmpConfigPayload, CmpEvent, ConsentSnapshot, ConsentState } from "./types";
import { CONSENT_SCHEMA_VERSION } from "./types";
import { BannerUI, setDisclosureBase } from "./ui";
import { acceptAllState, rejectAllState, defaultChoicesState, buildTcString } from "./tcstring";
import {
  readConsentState, writeConsentState, readMeta, writeMeta,
  readTcString, writeTcString, readGppString, purgeCookies
} from "./cookies";
import { applyConsentDefault, updateGoogleConsentMode } from "./gcm";
import { initGpp, setUsOptOut, isOptedOut } from "./gpp";
import { scanAndHold, releaseAll, startAutoBlocking, cookiePatternsToPurge, setInternalHost } from "./blocking";
import { Emitter } from "./events";
import { log, store } from "./debug";
import { openConsole, closeConsole } from "./console_loader";
import {
  applyTcUpdate,
  buildHostedGvl,
  installGetVendorListOverride,
  type HostedGvl
} from "./hosted_gvl";

type QueueCmd = [string, ...unknown[]];

/**
 * Fail closed: "none" (implicit accept, no banner) is only allowed when the
 * server explicitly detected a country. An unknown/undetectable country
 * (null, "", "XX", …) must show the strictest banner (tcf_eu) instead.
 */
export function failClosedRegime<T extends { regime: string; country?: string | null; regimesEnabled?: string[] }>(config: T): T {
  if (!config) return config;
  const country = typeof config.country === "string" ? config.country.trim().toUpperCase() : "";
  const known = /^[A-Z]{2}$/.test(country) && !["XX", "ZZ", "T1", "A1", "A2", "O1", "AP", "EU"].includes(country);
  if (!config.regime || (config.regime === "none" && !known)) {
    log("consent", `regime ${config.regime || "(missing)"} with unknown country → fail closed to tcf_eu`);
    return { ...config, regime: "tcf_eu" };
  }
  return config;
}

function isTcfRegime(regime: string) {
  return regime === "tcf_eu" || regime === "tcf_uk" || regime === "ch";
}

/** Why the banner must be shown again (null = stored consent is still valid). */
function resurfaceReason(config: CmpConfigPayload, meta: ReturnType<typeof readMeta>, existing: ConsentState | null): string | null {
  if (!existing || !meta) return "no stored consent";
  if ((meta.schema || 1) !== CONSENT_SCHEMA_VERSION) return `consent schema changed (${meta.schema || 1} → ${CONSENT_SCHEMA_VERSION})`;
  if (meta.configVersion !== config.configVersion) return `config version changed (${meta.configVersion} → ${config.configVersion})`;
  // A bare GVL version bump (weekly) no longer re-prompts; only newly disclosed
  // vendors/purposes (below), config changes or consent age do. The TC string still
  // carries the new vendorListVersion once the user next saves.

  const currentVendors = new Set(config.gvl.vendors.map((v) => v.id));
  const storedVendors = new Set(meta.vendorIds || []);
  for (const id of currentVendors) {
    if (!storedVendors.has(id)) return `new vendor disclosed (${id})`;
  }
  const currentPurposes = new Set(config.gvl.purposes.map((p) => p.id));
  const storedPurposes = new Set(meta.purposeIds || []);
  for (const id of currentPurposes) {
    if (!storedPurposes.has(id)) return `new purpose disclosed (${id})`;
  }

  if (meta.consentedAt) {
    const ageMs = Date.now() - Date.parse(meta.consentedAt);
    const maxDays = config.consentMaxAgeDays || config.consentMaxAgeDefaultDays || 395;
    if (Number.isFinite(ageMs) && ageMs > maxDays * 86400000) return `consent older than ${maxDays} days`;
  }
  return null;
}

/** true if any previously granted consent / opt-in / custom vendor is now denied. */
export function isWithdrawal(prev: ConsentState | null, next: ConsentState): boolean {
  if (!prev) return false;
  const lost = (a: Record<string | number, boolean>, b: Record<string | number, boolean>) =>
    Object.entries(a || {}).some(([k, v]) => v && !b?.[k as never]);
  return lost(prev.purposeConsents, next.purposeConsents) ||
    lost(prev.vendorConsents, next.vendorConsents) ||
    lost(prev.specialFeatureOptins, next.specialFeatureOptins) ||
    lost(prev.customVendors, next.customVendors);
}

export class CmpController {
  private apiBase: string;
  private propertyKey: string;
  private config?: CmpConfigPayload;
  private ui?: BannerUI;
  private cmpApi?: CmpApi;
  private hostedGvl?: HostedGvl;
  private state: ConsentState | null = null;
  private emitter = new Emitter();
  private ready = false;
  private consentScreen = 1;

  constructor(opts: { apiBase: string; propertyKey: string }) {
    this.apiBase = opts.apiBase.replace(/\/$/, "");
    this.propertyKey = opts.propertyKey;
    try { setInternalHost(new URL(this.apiBase).hostname); } catch { /* ignore */ }
    setDisclosureBase(this.apiBase);
    store.ctx = () => ({
      apiBase: this.apiBase,
      propertyKey: this.propertyKey,
      config: this.config,
      state: this.state,
      ready: this.ready,
      consentScreen: this.consentScreen,
      bannerVisible: !!this.ui && this.uiVisible,
      tcString: readTcString(),
      gppString: readGppString(),
      meta: readMeta()
    });
  }

  private uiVisible = false;

  async init(prefetched?: CmpConfigPayload) {
    this.config = failClosedRegime(prefetched || await this.fetchConfig());
    const gcmDefault = applyConsentDefault(
      this.config.regime,
      this.config.gcm?.wait_for_update || 500,
      this.config.gcm?.default_outside_opt_in === "granted"
    );
    log("gcm", "consent default", gcmDefault);

    if (this.config.regime === "gpp_us") initGpp(false, this.config.cmpId);

    this.cmpApi = new CmpApi(this.config.cmpId, this.config.cmpVersion, true);
    this.traceTcfApi();
    if (isTcfRegime(this.config.regime)) {
      this.hostedGvl = buildHostedGvl(this.config);
      installGetVendorListOverride(() => this.hostedGvl, this.apiBase);
      // No CmpApi#update yet: the first update decides the initial eventStatus.
      // It must be exactly one of 'tcloaded' (valid stored TC string, no UI) or
      // 'cmpuishown' (UI surfaced) — never tcloaded when the UI will be shown and
      // never a spurious 'useractioncomplete' (CMP API v2, addEventListener).
    } else {
      this.cmpApi.update(null, false);
    }

    startAutoBlocking(this.config, () => this.state);
    scanAndHold(this.config, this.state);

    const existing = readConsentState();
    const meta = readMeta();
    const tc = readTcString();
    const reason = resurfaceReason(this.config, meta, existing);

    if (!reason && existing) {
      this.state = existing;
      log("consent", "stored consent valid → no banner", { consentedAt: meta?.consentedAt, consentId: meta?.consentId });
      if (isTcfRegime(this.config.regime)) this.tcUpdate(tc || buildTcString(this.config, existing), false, "tcloaded");
      log("gcm", "consent update (stored consent)", updateGoogleConsentMode(this.config, existing));
      releaseAll(this.config, existing);
      this.ui = undefined;
      this.ensureUi().showRevisit();
      this.ready = true;
      log("ready", "CMP ready");
      this.emitter.emit("ready", this.getConsent());
      return;
    }

    if (this.config.regime === "none") {
      log("consent", "regime none → implicit accept");
      this.persist(acceptAllState(this.config), { silent: true });
      this.ready = true;
      log("ready", "CMP ready");
      this.emitter.emit("ready", this.getConsent());
      return;
    }

    if (reason) log("consent", `banner required: ${reason}`);
    this.openBanner();
    this.ready = true;
    log("ready", "CMP ready");
    this.emitter.emit("ready", this.getConsent());
  }

  consentAll() {
    if (!this.config) return;
    log("action", "accept all");
    this.persist(acceptAllState(this.config));
  }
  declineAll() {
    if (!this.config) return;
    log("action", "decline all");
    if (this.config.regime === "gpp_us") {
      setUsOptOut(true);
      this.persist(rejectAllState(this.config));
    } else {
      this.persist(rejectAllState(this.config));
    }
  }
  show() { log("action", "show() → settings"); this.openBanner(true); }
  /** Open (default) or close the debug console. */
  console(open: unknown = true) {
    if (open === false || open === "close" || open === "off") closeConsole();
    else openConsole(this.apiBase);
  }
  getConsent(): ConsentSnapshot | null {
    if (!this.config) return null;
    return {
      regime: this.config.regime,
      purposes: this.state?.purposeConsents || {},
      vendors: this.state?.vendorConsents || {},
      purposeLegitimateInterests: this.state?.purposeLegitimateInterests || {},
      vendorLegitimateInterests: this.state?.vendorLegitimateInterests || {},
      customVendors: this.state?.customVendors || {},
      tcString: readTcString(),
      gppString: readGppString(),
      configVersion: this.config.configVersion,
      language: this.config.language
    };
  }
  hasConsent(vendorIdOrCustomKey: number | string): boolean {
    if (!this.state) return false;
    if (typeof vendorIdOrCustomKey === "number" || /^\d+$/.test(String(vendorIdOrCustomKey))) {
      return !!this.state.vendorConsents[+vendorIdOrCustomKey];
    }
    return !!this.state.customVendors[String(vendorIdOrCustomKey)];
  }
  on(event: CmpEvent, cb: (payload?: unknown) => void) { this.emitter.on(event, cb); }
  off(event: CmpEvent, cb: (payload?: unknown) => void) { this.emitter.off(event, cb); }
  async setLanguage(lang: string) {
    this.config = failClosedRegime(await this.fetchConfig(lang));
    if (isTcfRegime(this.config.regime)) {
      this.hostedGvl = buildHostedGvl(this.config);
    }
    if (this.ui) this.openBanner(true);
  }

  flushQueue(queue: QueueCmd[]) {
    for (const [method, ...args] of queue) {
      const fn = (this as unknown as Record<string, unknown>)[method];
      if (typeof fn === "function") (fn as Function).apply(this, args);
    }
  }

  private tcUpdate(encoded: string | null, uiVisible: boolean, label: string) {
    if (!this.cmpApi) return;
    applyTcUpdate(this.cmpApi, this.hostedGvl, encoded, uiVisible);
    const eventStatus = uiVisible ? "cmpuishown" : label === "save" ? "useractioncomplete" : encoded ? "tcloaded" : "(no TC string)";
    log("tcf", `TC update → ${eventStatus}`, { label, length: encoded ? encoded.length : 0 });
  }

  /** Record third-party __tcfapi calls (command + version only) for the console. */
  private traceTcfApi() {
    const w = window as unknown as { __tcfapi?: (...a: unknown[]) => unknown };
    const orig = w.__tcfapi;
    if (typeof orig !== "function" || (orig as { __traced?: boolean }).__traced) return;
    const traced = function (this: unknown, ...args: unknown[]) {
      try { log("tcfapi", `__tcfapi('${String(args[0])}', ${String(args[1])})`); } catch { /* ignore */ }
      return orig.apply(this, args);
    } as typeof orig & { __traced?: boolean; __orig?: typeof orig };
    traced.__traced = true;
    traced.__orig = orig; // console reads through this without polluting the log
    w.__tcfapi = traced;
  }

  private openBanner(settings = false) {
    if (!this.config) return;
    if (this.config.regime === "none") return;
    const initial = this.state || defaultChoicesState(this.config);
    this.consentScreen = settings ? 2 : 1;
    this.ui?.destroy();
    this.ui = new BannerUI(this.config, initial, {
      onAcceptAll: () => { log("ui", "click: accept all"); this.consentAll(); },
      onRejectAll: () => { log("ui", "click: reject all"); this.declineAll(); },
      onOpenSettings: () => { log("ui", "banner layer 2 (settings)"); this.consentScreen = 2; this.ui?.showSecondLayer(); },
      onCloseSettings: () => { log("ui", "banner layer 1"); this.consentScreen = 1; this.ui?.showFirstLayer(); },
      onSave: (state) => { log("ui", "click: save selection"); this.persist(state); },
      onDoNotSell: () => { setUsOptOut(true); this.declineAll(); }
    });
    this.ui.setPriorDecision(!!this.state);
    if (settings) this.ui.showSecondLayer();
    else this.ui.showFirstLayer();
    this.uiVisible = true;
    log("ui", `banner shown (layer ${settings ? 2 : 1})`);

    if (isTcfRegime(this.config.regime) && this.cmpApi) {
      // cmpuishown: re-surfaced by the user → current TC string; otherwise a TC
      // string with all disclosed LI signals true and all consents false (spec).
      const current = this.state ? readTcString() : null;
      this.tcUpdate(current || buildTcString(this.config, defaultChoicesState(this.config), this.consentScreen), true, "shown");
    }
    this.emitter.emit("shown");
  }

  private ensureUi() {
    if (!this.ui && this.config) {
      this.ui = new BannerUI(this.config, this.state || defaultChoicesState(this.config), {
        onAcceptAll: () => { log("ui", "click: accept all"); this.consentAll(); },
        onRejectAll: () => { log("ui", "click: reject all"); this.declineAll(); },
        onOpenSettings: () => { log("ui", "click: privacy settings (revisit)"); this.openBanner(true); },
        onCloseSettings: () => this.ui?.showFirstLayer(),
        onSave: (state) => this.persist(state),
        onDoNotSell: () => { setUsOptOut(true); this.declineAll(); }
      });
    }
    return this.ui!;
  }

  private async persist(state: ConsentState, opts: { silent?: boolean } = {}) {
    if (!this.config) return;
    const withdrawn = isWithdrawal(this.state, state);
    this.state = state;
    writeConsentState(state);

    let tc = "";
    if (isTcfRegime(this.config.regime)) {
      tc = buildTcString(this.config, state, this.consentScreen);
      writeTcString(tc);
      this.tcUpdate(tc, false, "save"); // useractioncomplete
    }

    if (this.config.regime === "gpp_us") {
      setUsOptOut(isOptedOut() || Object.values(state.customVendors).every((v) => !v));
    }

    writeMeta({
      configVersion: this.config.configVersion,
      gvlVersion: this.config.gvl.vendorListVersion,
      consentId: crypto.randomUUID(),
      regime: this.config.regime,
      consentedAt: new Date().toISOString(),
      vendorIds: this.config.gvl.vendors.map((v) => v.id),
      purposeIds: this.config.gvl.purposes.map((p) => p.id),
      schema: CONSENT_SCHEMA_VERSION
    });

    const count = (m: Record<string | number, boolean>) => Object.values(m || {}).filter(Boolean).length;
    log("consent", `consent saved: ${count(state.purposeConsents)} purposes, ${count(state.vendorConsents)} vendors, ${count(state.customVendors)} custom`, {
      purposes: count(state.purposeConsents), vendors: count(state.vendorConsents),
      customVendors: count(state.customVendors), specialFeatures: count(state.specialFeatureOptins), tcLength: tc.length
    });
    log("gcm", "consent update", updateGoogleConsentMode(this.config, state));
    releaseAll(this.config, state);
    const purge = cookiePatternsToPurge(this.config, state);
    purgeCookies(purge);
    if (purge.length) log("cookies", `purged cookie patterns of declined vendors: ${purge.join(", ")}`);

    this.ui?.destroy();
    this.ui = undefined;
    this.uiVisible = false;
    if (!opts.silent) {
      this.ensureUi().showRevisit();
      log("ui", "banner hidden, revisit button shown");
      this.emitter.emit("hidden");
      this.emitter.emit("consent", this.getConsent());
    }

    try {
      const started = performance.now();
      const res = await fetch(`${this.apiBase}/c/${encodeURIComponent(this.propertyKey)}/consents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          consent_id: readMeta()?.consentId,
          tc_string: tc,
          gpp_string: readGppString(),
          config_version: this.config.configVersion,
          gvl_version: this.config.gvl.vendorListVersion,
          language: this.config.language,
          country: this.config.country,
          regime: this.config.regime,
          purposes: { consented: state.purposeConsents, legitimate_interests: state.purposeLegitimateInterests },
          vendors: { consented: state.vendorConsents, legitimate_interests: state.vendorLegitimateInterests },
          custom_vendors: state.customVendors
        })
      });
      log("net", `consent log POST → ${res.status} (${Math.round(performance.now() - started)} ms)`);
    } catch (err) {
      log("error", `consent log failed: ${err instanceof Error ? err.message : String(err)}`);
      console.warn(`[${SDK_NAME}] consent log failed`, err);
    }

    // Scripts/iframes released earlier cannot be "un-run": after a withdrawal
    // (any consent/opt-in switched from granted to denied) reload so gated
    // elements are held again. Publishers can disable via ui.reloadOnWithdraw=false.
    const reloadPref = this.config.banner.ui?.reloadOnWithdraw as unknown;
    if (withdrawn && !opts.silent && reloadPref !== false && reloadPref !== "false") {
      setTimeout(() => location.reload(), 300);
    }
  }

  private async fetchConfig(lang?: string) {
    const url = new URL(`${this.apiBase}/c/${encodeURIComponent(this.propertyKey)}/config.json`);
    const detected =
      lang ||
      document.documentElement.lang ||
      (typeof navigator !== "undefined" ? navigator.language : "") ||
      "";
    if (detected) url.searchParams.set("lang", detected.split("-")[0]);
    const boot = (window as unknown as { __CMP_BASE__?: { country?: string } }).__CMP_BASE__;
    if (boot?.country) url.searchParams.set("country", boot.country);
    const started = performance.now();
    log("net", `config.json GET ${url.search}`);
    const res = await fetch(url);
    const ms = Math.round(performance.now() - started);
    if (!res.ok) {
      log("error", `config.json → ${res.status} (${ms} ms)`);
      throw new Error(`config fetch failed: ${res.status}`);
    }
    const json = (await res.json()) as CmpConfigPayload;
    log("net", `config.json → ${res.status} (${ms} ms), regime ${json.regime}, lang ${json.language}, v${json.configVersion}`, {
      ms, cache: res.headers.get("cache-control"), age: res.headers.get("age")
    });
    log("metering", "pageview metered server-side (config.json)");
    return json;
  }
}

export function attachPublicApi(controller: CmpController) {
  const w = window as unknown as Record<string, unknown>;
  const existing = w[SDK_NAME] as { q?: QueueCmd[] } | undefined;
  const queue = Array.isArray(existing?.q) ? existing!.q! : [];

  const api = {
    consentAll: () => controller.consentAll(),
    declineAll: () => controller.declineAll(),
    show: () => controller.show(),
    /** Re-open the consent dialog (alias of show, for "Cookie-Einstellungen" links). */
    open: () => controller.show(),
    getConsent: () => controller.getConsent(),
    hasConsent: (id: number | string) => controller.hasConsent(id),
    on: (event: CmpEvent, cb: (payload?: unknown) => void) => controller.on(event, cb),
    off: (event: CmpEvent, cb: (payload?: unknown) => void) => controller.off(event, cb),
    setLanguage: (lang: string) => controller.setLanguage(lang),
    console: (open?: unknown) => controller.console(open),
    q: [] as QueueCmd[]
  };

  w[SDK_NAME] = api;
  controller.flushQueue(queue);

  // Declarative re-open: <a href="#" data-adconsent-open>Cookie-Einstellungen</a>
  document.addEventListener("click", (ev) => {
    const el = (ev.target as Element | null)?.closest?.("[data-adconsent-open]");
    if (!el) return;
    ev.preventDefault();
    controller.show();
  });
}
